import {
  type Agent,
  type AgentLaunchKind,
  HttpError,
  type IconColorKey,
  isChatStatusBusy,
  MAIN_CHAT_MODE,
} from '@littlebird/sdk';
import { computed, makeAutoObservable, runInAction, when } from 'mobx';
import { v4 as uuidv4 } from 'uuid';

import { logError } from '@/lib/errorLogging';
import { littlebirdSdk } from '@/lib/sdk-init';

import { type BirdLook, birdLookForAgent } from './agents-bird.look';

export type AgentEntry = {
  agentId: string;
  chatId: string | undefined;
  name: string;
  label: string | null;
  description: string | null;
  bird: BirdLook;
  createdAt: string;
  pinnedAt: string | null;
  archivedAt: string | null;
};

export type LaunchOutcome = { chatId: string; routedTo?: AgentEntry };

export type LaunchRecord = {
  id: string;
  query: string;
  sentAt: string;
  chatId?: string;
  agentId?: string;
  kind?: 'routed' | 'new';
  sendState?: 'pending' | 'failed';
};

export const LAUNCHED_AGENT_NAME = 'New agent';

const MAX_LAUNCHES = 30;

const capLaunches = (records: LaunchRecord[]) => {
  let settled = 0;
  return records.filter((record) => {
    if (record.sendState !== undefined) return true;
    settled += 1;
    return settled <= MAX_LAUNCHES;
  });
};

class SessionEndedError extends Error {
  constructor() {
    super('The session ended before the task was started');
  }
}

export class LaunchNotStartedError extends Error {
  constructor(cause: unknown) {
    super('The task was not started', { cause });
  }
}

export type AgentsNavItem =
  | 'feed'
  | 'meetings'
  | 'chats'
  | 'routines'
  | 'projects'
  | 'integrations'
  | 'library';

const DEFAULT_NAV_ITEMS: AgentsNavItem[] = ['feed', 'meetings'];

export const ROUTED_SEND_HOLD_MS = 4000;

type RoutedSend = {
  launchId: string;
  chatId: string;
  query: string;
  timer: ReturnType<typeof setTimeout>;
};

let routedSend: RoutedSend | undefined;

type QueuedSend = { launchId: string; query: string };

const sendQueues = new Map<string, QueuedSend[]>();
const queuedLaunchIds = new Set<string>();
let sessionGeneration = 0;

export const LAUNCH_DRAFT_ID = 'agents-launch';

export const launchDraftId = (launchId: string) =>
  `${LAUNCH_DRAFT_ID}:${launchId}`;

let draftMoves: Promise<void> = Promise.resolve();

const takeComposerDraft = (launchId: string) => {
  const move = draftMoves.then(() =>
    littlebirdSdk.stores.drafts.moveDraft(
      LAUNCH_DRAFT_ID,
      launchDraftId(launchId)
    )
  );
  draftMoves = move.catch(() => undefined);
  return move;
};

const sendToChat = async (
  chatId: string,
  content: string,
  launchId: string
) => {
  const { threads, messages, drafts } = littlebirdSdk.stores;
  const draftId = launchDraftId(launchId);
  await drafts.updateDraft({ draftId, content });
  await messages.sendMessage({
    threadId: chatId,
    draftId,
    mode: threads.getById(chatId)?.model || MAIN_CHAT_MODE,
    useTodos: false,
  });
};

const STORAGE_KEY = 'littlebird-agents-poc';

const PROFILE_DEFAULT_WIDTH = 340;

type AgentLocalMeta = {
  chatId?: string;
  lastViewedAt?: string;
};

type LegacyPersistedAgent = {
  chatId: string;
  name: string;
  createdAt: string;
};

type PersistedAgentsState = {
  meta?: Record<string, AgentLocalMeta>;
  agents?: LegacyPersistedAgent[];
  launches?: LaunchRecord[];
  profileOpen?: boolean;
  profileWidth?: number;
  navItems?: AgentsNavItem[];
};

class AgentsStore {
  heldLaunchId: string | undefined = undefined;
  isProfileOpen = false;
  profileWidth = PROFILE_DEFAULT_WIDTH;
  navItems: AgentsNavItem[] = DEFAULT_NAV_ITEMS;
  private launchLog: LaunchRecord[] = [];
  private meta: Record<string, AgentLocalMeta> = {};
  private legacyAgents: LegacyPersistedAgent[] = [];

  constructor() {
    makeAutoObservable<this, 'entriesByChatId'>(this, {
      entriesByChatId: computed.struct,
    });
    this.hydrate();
  }

  private get sdkAgents() {
    return littlebirdSdk.stores.agents;
  }

  get isRosterLoaded() {
    return this.sdkAgents.isLoaded;
  }

  async addDefaultTeam(): Promise<void> {
    try {
      await this.sdkAgents.addDefaultTeam();
    } catch (error) {
      if (error instanceof HttpError && error.status === 409) return;
      throw error;
    }
  }

  get agents(): AgentEntry[] {
    return this.sdkAgents.orderedData.map((agent) => this.toEntry(agent));
  }

  get pinned(): AgentEntry[] {
    return this.agents
      .filter((agent) => agent.pinnedAt !== null && agent.archivedAt === null)
      .sort((a, b) => (b.pinnedAt ?? '').localeCompare(a.pinnedAt ?? ''));
  }

  get recent(): AgentEntry[] {
    return this.agents.filter(
      (agent) => agent.pinnedAt === null && agent.archivedAt === null
    );
  }

  get launches(): LaunchRecord[] {
    return this.launchLog.filter((record) => {
      if (record.agentId === undefined || record.sendState !== undefined)
        return true;
      if (record.id === this.heldLaunchId) return true;
      const agent = this.sdkAgents.getById(record.agentId);
      return agent !== undefined && agent.archivedAt === null;
    });
  }

  private toEntry(agent: Agent): AgentEntry {
    return {
      agentId: agent.id,
      chatId: this.resolveChatId(agent),
      name: agent.name,
      label: agent.label,
      description: agent.description,
      bird: birdLookForAgent(agent),
      createdAt: agent.createdAt.toISOString(),
      pinnedAt: agent.pinnedAt?.toISOString() ?? null,
      archivedAt: agent.archivedAt?.toISOString() ?? null,
    };
  }

  private resolveChatId(agent: Agent): string | undefined {
    const thread = littlebirdSdk.stores.threads.getThreadForAgent(agent.id);
    return thread?.id ?? agent.chatId ?? this.meta[agent.id]?.chatId;
  }

  getById(agentId: string): AgentEntry | undefined {
    const agent = this.sdkAgents.getById(agentId);
    return agent ? this.toEntry(agent) : undefined;
  }

  // Structural, so the many readers keyed by chat (one per open tab) only
  // rerun when an agent's chat mapping changes, not on every thread update
  private get entriesByChatId(): Map<string, AgentEntry> {
    const entries = new Map<string, AgentEntry>();
    for (const entry of this.agents) {
      if (entry.chatId !== undefined && !entries.has(entry.chatId)) {
        entries.set(entry.chatId, entry);
      }
    }
    return entries;
  }

  getByChatId(chatId: string): AgentEntry | undefined {
    const agentId = littlebirdSdk.stores.threads.getById(chatId)?.agentId;
    const direct = agentId ? this.getById(agentId) : undefined;
    return direct?.chatId === chatId
      ? direct
      : this.entriesByChatId.get(chatId);
  }

  lastViewedAt(agentId: string): string | undefined {
    return this.meta[agentId]?.lastViewedAt;
  }

  markViewed(agentId: string, at: string) {
    if (this.meta[agentId]?.lastViewedAt === at) return;
    this.meta[agentId] = { ...this.meta[agentId], lastViewedAt: at };
    this.persist();
  }

  setProfileOpen(open: boolean) {
    this.isProfileOpen = open;
    this.persist();
  }

  setProfileWidth(width: number) {
    this.profileWidth = width;
  }

  saveProfileWidth() {
    this.persist();
  }

  toggleNavItem(item: AgentsNavItem) {
    this.navItems = this.navItems.includes(item)
      ? this.navItems.filter((shown) => shown !== item)
      : [...this.navItems, item];
    this.persist();
  }

  async updateAgent(
    agentId: string,
    {
      iconColor,
      ...fields
    }: {
      name?: string;
      label?: string | null;
      description?: string | null;
      iconColor?: IconColorKey;
    }
  ): Promise<void> {
    const agent = this.sdkAgents.getById(agentId);
    if (!agent) return;
    await this.sdkAgents.updateAgent(
      agent,
      iconColor
        ? {
            ...fields,
            icon: { ...(agent.icon ?? { type: 'icon' }), color: iconColor },
          }
        : fields,
      { notifyAgent: true }
    );
  }

  async removeAgent(agentId: string): Promise<void> {
    await this.sdkAgents.deleteAgent(agentId);
    runInAction(() => {
      delete this.meta[agentId];
    });
    this.persist();
  }

  async setPinned(agentId: string, pinned: boolean): Promise<void> {
    const agent = this.sdkAgents.getById(agentId);
    if (agent) await this.sdkAgents.updateAgentLifecycle(agent, { pinned });
  }

  async setArchived(agentId: string, archived: boolean): Promise<void> {
    const agent = this.sdkAgents.getById(agentId);
    if (agent) await this.sdkAgents.updateAgentLifecycle(agent, { archived });
  }

  async launch(query: string): Promise<LaunchOutcome> {
    this.flushRoutedSend();
    const record: LaunchRecord = {
      id: uuidv4(),
      query,
      sentAt: new Date().toISOString(),
    };
    try {
      await takeComposerDraft(record.id);
    } catch (error) {
      throw new LaunchNotStartedError(error);
    }
    runInAction(() => {
      this.launchLog = capLaunches([record, ...this.launchLog]);
    });
    this.persist();
    return this.runLaunch(record);
  }

  private async runLaunch(record: LaunchRecord): Promise<LaunchOutcome> {
    const { query } = record;
    const generation = sessionGeneration;
    try {
      const decision = await this.sdkAgents.routeLaunch(query);
      const target =
        decision.route === 'existing'
          ? await this.resolveRoutedAgent(decision.agent_id)
          : undefined;
      if (generation !== sessionGeneration) throw new SessionEndedError();
      if (target) {
        this.holdRoutedSend(record.id, target.chatId, query);
        this.settleLaunch(
          record.id,
          target.chatId,
          target.agentId,
          'routed',
          'pending'
        );
        return { chatId: target.chatId, routedTo: target };
      }
      const kind = decision.route === 'new' ? decision.kind : 'task';
      const chatId = await this.launchNewAgent(record.id, query, kind);
      return { chatId };
    } catch (error) {
      if (!(error instanceof SessionEndedError))
        this.setSendState(record.id, 'failed');
      throw error;
    }
  }

  flushRoutedSend() {
    if (!routedSend) return;
    clearTimeout(routedSend.timer);
    const { launchId, chatId, query } = routedSend;
    routedSend = undefined;
    this.heldLaunchId = undefined;
    this.queueSend(chatId, { launchId, query });
  }

  async undoRoutedSend(): Promise<string | undefined> {
    if (!routedSend) return undefined;
    clearTimeout(routedSend.timer);
    const { launchId, query } = routedSend;
    routedSend = undefined;
    this.heldLaunchId = undefined;
    runInAction(() => {
      this.launchLog = this.launchLog.map((row) =>
        row.id === launchId
          ? { id: row.id, query: row.query, sentAt: row.sentAt, kind: 'new' }
          : row
      );
    });
    this.persist();
    try {
      return await this.launchNewAgent(launchId, query, 'task');
    } catch (error) {
      this.setSendState(launchId, 'failed');
      throw error;
    }
  }

  async retrySend(launchId: string): Promise<void> {
    const record = this.launchLog.find((row) => row.id === launchId);
    if (!record) return;
    if (record.chatId) {
      this.setSendState(launchId, 'pending');
      this.resumePendingSends();
      return;
    }
    this.setSendState(launchId, undefined);
    if (record.kind === 'new') {
      try {
        await this.launchNewAgent(launchId, record.query, 'task');
      } catch (error) {
        this.setSendState(launchId, 'failed');
        throw error;
      }
      return;
    }
    await this.runLaunch(record);
  }

  resumePendingSends() {
    const { threads } = littlebirdSdk.stores;
    const generation = sessionGeneration;
    for (const record of [...this.launchLog].reverse()) {
      const { id, chatId, query } = record;
      if (record.sendState !== 'pending' || !chatId) continue;
      if (id === routedSend?.launchId || queuedLaunchIds.has(id)) continue;
      queuedLaunchIds.add(id);
      threads.ensureLoaded(chatId).then(
        (thread) => {
          if (generation !== sessionGeneration) return;
          queuedLaunchIds.delete(id);
          if (thread) this.queueSend(chatId, { launchId: id, query });
          else this.setSendState(id, 'failed');
        },
        (error: unknown) => {
          if (generation !== sessionGeneration) return;
          queuedLaunchIds.delete(id);
          this.setSendState(id, 'failed');
          logError(error, 'Failed to resume a launched task', {
            context: 'AgentsStore',
          });
        }
      );
    }
  }

  private queueSend(chatId: string, send: QueuedSend) {
    queuedLaunchIds.add(send.launchId);
    const queue = sendQueues.get(chatId);
    if (queue) {
      queue.push(send);
      return;
    }
    sendQueues.set(chatId, [send]);
    void this.drainSends(chatId);
  }

  private async drainSends(chatId: string) {
    const { threads } = littlebirdSdk.stores;
    const generation = sessionGeneration;
    const queue = sendQueues.get(chatId) ?? [];
    await threads.ensureLoaded(chatId).catch(() => undefined);
    if (generation !== sessionGeneration) return;
    while (queue.length > 0) {
      await when(() => !isChatStatusBusy(threads.get(chatId)?.status));
      if (generation !== sessionGeneration) return;
      const next = queue.shift();
      if (!next) break;
      this.setSendState(next.launchId, undefined);
      try {
        await sendToChat(chatId, next.query, next.launchId);
      } catch (error) {
        if (generation !== sessionGeneration) return;
        logError(error, 'Failed to send a launched task', {
          context: 'AgentsStore',
        });
        this.setSendState(next.launchId, 'failed');
      }
      if (generation !== sessionGeneration) return;
      queuedLaunchIds.delete(next.launchId);
    }
    sendQueues.delete(chatId);
  }

  private holdRoutedSend(launchId: string, chatId: string, query: string) {
    this.flushRoutedSend();
    this.heldLaunchId = launchId;
    routedSend = {
      launchId,
      chatId,
      query,
      timer: setTimeout(() => this.flushRoutedSend(), ROUTED_SEND_HOLD_MS),
    };
  }

  private async launchNewAgent(
    launchId: string,
    query: string,
    kind: AgentLaunchKind
  ): Promise<string> {
    const generation = sessionGeneration;
    const agent = await this.sdkAgents.createLaunchedAgent(kind, query);
    if (generation !== sessionGeneration) throw new SessionEndedError();
    if (!agent.chatId) throw new Error('Launched agent has no chat');
    this.settleLaunch(launchId, agent.chatId, agent.id, 'new', 'pending');
    this.queueSend(agent.chatId, { launchId, query });
    return agent.chatId;
  }

  private settleLaunch(
    launchId: string,
    chatId: string,
    agentId: string,
    kind: LaunchRecord['kind'],
    sendState?: LaunchRecord['sendState']
  ) {
    runInAction(() => {
      this.launchLog = this.launchLog.map((row) =>
        row.id === launchId ? { ...row, chatId, agentId, kind, sendState } : row
      );
    });
    this.persist();
  }

  private setSendState(launchId: string, sendState: LaunchRecord['sendState']) {
    runInAction(() => {
      this.launchLog = this.launchLog.map((row) =>
        row.id === launchId ? { ...row, sendState } : row
      );
    });
    this.persist();
  }

  private async resolveRoutedAgent(
    agentId: string
  ): Promise<AgentEntry & { chatId: string }> {
    const hasChat = (
      entry: AgentEntry | undefined
    ): entry is AgentEntry & { chatId: string } => Boolean(entry?.chatId);
    const local = this.getById(agentId);
    if (hasChat(local)) return local;
    await littlebirdSdk.syncNow();
    const synced = this.getById(agentId);
    if (!hasChat(synced))
      throw new Error('The agent that owns this task is not on this device');
    return synced;
  }

  reset() {
    if (routedSend) clearTimeout(routedSend.timer);
    routedSend = undefined;
    sendQueues.clear();
    queuedLaunchIds.clear();
    sessionGeneration += 1;
    runInAction(() => {
      this.heldLaunchId = undefined;
      this.launchLog = [];
      this.meta = {};
      this.legacyAgents = [];
    });
  }

  migrateLegacyRegistry() {
    const legacy = this.legacyAgents;
    if (legacy.length === 0) return;
    this.legacyAgents = [];
    for (const entry of legacy) {
      const agent = this.sdkAgents.createAgent({
        name: entry.name,
        chatId: entry.chatId,
      });
      this.meta[agent.id] = { chatId: entry.chatId };
    }
    this.persist();
  }

  private hydrate() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const persisted = JSON.parse(raw) as PersistedAgentsState;
      this.meta = persisted.meta ?? {};
      this.legacyAgents = persisted.agents ?? [];
      this.isProfileOpen = persisted.profileOpen ?? false;
      this.profileWidth = persisted.profileWidth ?? PROFILE_DEFAULT_WIDTH;
      this.navItems = persisted.navItems ?? DEFAULT_NAV_ITEMS;
      this.launchLog = (persisted.launches ?? []).map((record) =>
        record.chatId === undefined && record.sendState === undefined
          ? { ...record, sendState: 'failed' }
          : record
      );
    } catch {}
  }

  private persist() {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        meta: this.meta,
        launches: this.launchLog,
        profileOpen: this.isProfileOpen,
        profileWidth: this.profileWidth,
        navItems: this.navItems,
      })
    );
  }
}

export const agentsStore = new AgentsStore();
